import React, { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { IoMdAlert } from '@react-icons/all-files/io/IoMdAlert'
import { FiChevronRight } from '@react-icons/all-files/fi/FiChevronRight'
import { MdClose } from '@react-icons/all-files/md/MdClose'
import todayIsBetweenDates from 'utils/dates/today-is-between-dates'
import getCookie from 'utils/cookies/get-cookie'
import setCookie from 'utils/cookies/set-cookie'
import UnstyledButton from 'components/button/unstyled-button'
import { headerAnimationDuration } from '.'
import { useIsMounted } from 'hooks/use-is-mounted'
import { useEffectOnce } from 'hooks/use-effect-once'

const COOKIE_ID = 'notificationBar'

const START_DATE = new Date('2024-05-01T00:00:00-06:00')
const END_DATE = new Date('2025-01-01T00:00:00-06:00')

const NOTIFICATION_TEXT = 'Our Mayfield, KY store is open!'
const NOTIFICATION_LINK = '/locations/mayfield/'
const NOTIFICATION_LINK_TEXT = 'Learn more'

const Notifications = ({ ...props }) => {
  const [hidden, setHidden] = useState(true)
  const [expired, setExpired] = useState(false)
  const [animationActive, setAnimationActive] = useState(false)
  const isMounted = useIsMounted()

  useEffectOnce(() => {
    // Check if user closed the notification bar
    const closed = getCookie(COOKIE_ID)

    const expired = !todayIsBetweenDates({ start: START_DATE, end: END_DATE })

    setExpired(expired)

    if (!closed && !expired) {
      setHidden(false)
    }
  })

  const close = () => {
    setAnimationActive(true)
    setCookie(COOKIE_ID, true, END_DATE)

    setTimeout(() => {
      if (isMounted()) {
        setAnimationActive(false)
        setHidden(true)
      }
    }, headerAnimationDuration)
  }

  if (hidden || expired) {
    return <></>
  }
  return (
    <NotificationsContainer animationActive={animationActive} {...props}>
      <NotificationsWrapper>
        <NotificationCol>
          <NotificationIcon aria-hidden='true' focusable='false' role='presentation' />
        </NotificationCol>
        <NotificationCol>
          <Notification>{NOTIFICATION_TEXT}</Notification>
          <NotificationLink to={NOTIFICATION_LINK}>
            <NotificationLinkText>{NOTIFICATION_LINK_TEXT}</NotificationLinkText>
            <FiChevronRight aria-hidden='true' focusable='false' role='presentation' />
          </NotificationLink>
        </NotificationCol>
      </NotificationsWrapper>
      <CloseNotifications onClick={close} aria-label='Close notification bar'>
        <MdClose aria-hidden='true' focusable='false' role='presentation' />
      </CloseNotifications>
    </NotificationsContainer>
  )
}

const NotificationsContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-height: ${props => (props.animationActive ? '0' : '200px')};
  overflow: hidden;
  position: relative;
  transition: ${props => props.theme.transition};

  @media (min-width: 900px) {
    display: block;
    max-height: auto;
    overflow: auto;
    opacity: 0;
    padding: 0;
    top: 50%;
    transform: translateY(-50%);

    &.original-elements-active {
      opacity: 1;
    }
  }
`

const NotificationsWrapper = styled.div`
  display: flex;
  flex-grow: 2;
  padding: 15px;

  @media (min-width: 900px) {
    flex-grow: 0;
    padding: 0;
  }
`

const CloseNotifications = styled(UnstyledButton)`
  flex-shrink: 0;
  padding: 0 15px;

  svg {
    color: rgba(255, 255, 255, 0.9);
    font-size: 24px;
  }

  @media (min-width: 900px) {
    display: none !important;
  }
`

const NotificationCol = styled.div`
  display: inline-block;
  height: 100%;
`

const Notification = styled.span`
  color: rgba(255, 255, 255, 0.9);
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  padding: 7px 10px 7px 0;
  vertical-align: middle;
`

const NotificationIcon = styled(IoMdAlert)`
  color: #fd0;
  font-size: 18px;
  margin: 5px 5px 0 0;
  vertical-align: middle;
`

const NotificationLink = styled(Link)`
  background-color: transparent;
  border: 1px solid #fd0;
  color: #fd0;
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  padding: 3px 3px 3px 5px;
  text-decoration: none;
  transition: ${props => props.theme.transition};
  vertical-align: middle;

  :hover,
  :focus {
    background-color: #fd0;
    color: #161414;

    svg {
      color: #161414;
    }
  }

  svg {
    color: #fd0;
    transition: ${props => props.theme.transition};
    vertical-align: middle;
  }
`

const NotificationLinkText = styled.span`
  vertical-align: middle;
`

export default Notifications
